// src/pages/PortalJobsPage.js — Member portal → Job Board: post, edit and remove your company's job posts
// [marker: finish-up v1]
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import portalApi from '../utils/portalApi';
import toast from 'react-hot-toast';
import { JOB_TYPE_LABELS, WORKPLACE_LABELS } from './PublicJobsPage';

const EMPTY = { title: '', companyName: '', location: '', jobType: 'full_time', workplace: 'on_site', salary: '', description: '', howToApply: '', applyEmail: '', applyUrl: '', companyWebsite: '' };
const fmtDay = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

export default function PortalJobsPage() {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // 'new' | post
  const load = () => portalApi.get('/portal-jobs/mine').then(r => { setData(r.data); return r.data; }).catch(() => toast.error('Could not load your job posts.'));
  useEffect(() => {
    load().then(d => { if (d && d.canPost && new URLSearchParams(location.search).get('new')) setEditing('new'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const remove = async (p) => {
    if (!window.confirm(`Remove “${p.title}” from the job board?`)) return;
    try { await portalApi.delete(`/portal-jobs/${p.id}`); toast.success('Removed.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not remove it.'); }
  };
  if (!data) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Job Board</h1>
          <p className="page-subtitle">Post openings at {data.businessName} on the NJJBA job board — free for members. Posts stay up until you remove them.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a className="btn btn-secondary" href="/jobs" target="_blank" rel="noreferrer">View the board ↗</a>
          {data.canPost && <button className="btn btn-primary" onClick={() => setEditing('new')}>+ Post a job</button>}
        </div>
      </div>

      {!data.canPost && (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: '#fffbeb', borderColor: '#fde68a' }}>
          Only current NJJBA members can post jobs. Please contact NJJBA to renew your membership.
        </div>
      )}

      <div className="card">
        <div className="card-header"><span className="card-title">Your company’s posts ({data.posts.length})</span></div>
        {data.posts.length === 0 ? (
          <div className="empty-state" style={{ padding: 30 }}>
            <div className="empty-state-icon">💼</div>
            <h3>No job posts yet</h3>
            {data.canPost && <button className="btn btn-primary" onClick={() => setEditing('new')}>+ Post your first job</button>}
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Job</th><th>Type</th><th>Posted</th><th>By</th><th></th></tr></thead>
              <tbody>
                {data.posts.map(p => (
                  <tr key={p.id}>
                    <td><a href={`/jobs?job=${p.id}`} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>{p.title}</a><div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{p.location || ''}</div></td>
                    <td style={{ fontSize: '0.85rem' }}>{[JOB_TYPE_LABELS[p.job_type], WORKPLACE_LABELS[p.workplace]].filter(Boolean).join(' · ')}</td>
                    <td style={{ fontSize: '0.85rem' }}>{fmtDay(p.posted)}{p.updated !== p.posted ? <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>edited {fmtDay(p.updated)}</div> : null}</td>
                    <td style={{ fontSize: '0.85rem' }}>{p.posted_by || ''}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(p)}>Edit</button>{' '}
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => remove(p)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <JobForm post={editing === 'new' ? null : editing} businessName={data.businessName} website={data.website}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function JobForm({ post, businessName, website, onClose, onSaved }) {
  const [f, setF] = useState(() => post ? {
    title: post.title || '', companyName: post.company_name || '', location: post.location || '', jobType: post.job_type || '', workplace: post.workplace || '',
    salary: post.salary || '', description: post.description || '', howToApply: post.how_to_apply || '', applyEmail: post.apply_email || '', applyUrl: post.apply_url || '', companyWebsite: post.company_website || '',
  } : { ...EMPTY, companyName: businessName || '', companyWebsite: website || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) return toast.error('Enter the job title.');
    if (!f.description.trim()) return toast.error('Describe the job.');
    if (!f.applyEmail.trim() && !f.applyUrl.trim() && !f.howToApply.trim()) return toast.error('Tell people how to apply.');
    setSaving(true);
    try {
      if (post) await portalApi.patch(`/portal-jobs/${post.id}`, f); else await portalApi.post('/portal-jobs', f);
      toast.success(post ? 'Saved.' : 'Your job is on the board!');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setSaving(false); }
  };
  const field = (label, k, props = {}) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" value={f[k]} onChange={e => set(k, e.target.value)} {...props} />
    </div>
  );
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <span className="modal-title">{post ? 'Edit job post' : 'Post a job'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          <div className="form-row">
            {field('Job title *', 'title', { autoFocus: true, placeholder: 'e.g. Office Manager' })}
            {field('Company', 'companyName')}
          </div>
          <div className="form-row">
            {field('Location', 'location', { placeholder: 'e.g. Newark, NJ' })}
            {field('Salary / pay (optional)', 'salary', { placeholder: 'e.g. $55,000–$65,000 or $25/hour' })}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Job type</label>
              <select className="form-select" value={f.jobType} onChange={e => set('jobType', e.target.value)}>
                <option value="">—</option>
                {Object.entries(JOB_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Where</label>
              <select className="form-select" value={f.workplace} onChange={e => set('workplace', e.target.value)}>
                <option value="">—</option>
                {Object.entries(WORKPLACE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Job description *</label>
            <textarea className="form-textarea" rows={9} value={f.description} onChange={e => set('description', e.target.value)}
              placeholder="What the job is, responsibilities, requirements, benefits…" />
          </div>
          <div style={{ fontWeight: 700, margin: '6px 0 8px' }}>How to apply <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: '0.85rem' }}>(at least one)</span></div>
          <div className="form-row">
            {field('Email applications to', 'applyEmail', { type: 'email', placeholder: 'jobs@yourcompany.com' })}
            {field('Or apply at this link', 'applyUrl', { placeholder: 'https://…' })}
          </div>
          <div className="form-group">
            <label className="form-label">Other instructions (optional)</label>
            <textarea className="form-textarea" rows={2} value={f.howToApply} onChange={e => set('howToApply', e.target.value)} placeholder="e.g. Send a résumé and cover letter by November 30." />
          </div>
          {field('Company website (optional)', 'companyWebsite', { placeholder: 'https://…' })}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : post ? 'Save' : 'Post job'}</button>
        </div>
      </div>
    </div>
  );
}
